// @ts-strict-ignore
import { getProperty, setProperty } from 'dot-prop';
import cloneDeep from 'lodash/cloneDeep';
import { booleanRuleStrings } from '../../constants';
import type {
  BooleanRule,

  Rules,
} from '../../types';
import { correctAmbiguousBooleanRuleObject } from '~/dashboard/views/workflows/service/thresholds';
import { isBooleanRule } from 'flow/service/rules';

export function findThresholdIdAndUpdate({
  thresholdId,
  booleanRule,
}) {
  return booleanRule.reduce((result, ruleObject) => {
    if (ruleObject.id && ruleObject.id !== thresholdId) {
      result.push(ruleObject);
    }

    const firstOperatorKeyInTheOriginalRule = ruleObject.and
      ? booleanRuleStrings.AND_OPERATOR_STRING
      : booleanRuleStrings.OR_OPERATOR_STRING;
    if (ruleObject[firstOperatorKeyInTheOriginalRule]) {
      const updatedRule = findThresholdIdAndUpdate({
        thresholdId,
        booleanRule: ruleObject[firstOperatorKeyInTheOriginalRule],
      });

      if (updatedRule.length > 0) {
        result.push({
          [firstOperatorKeyInTheOriginalRule]: updatedRule,
          publicId: isBooleanRule(ruleObject) ? ruleObject.publicId : undefined,
        });
      }
    }

    return result;
  }, []);
}

export function addThresholdToBooleanRule({
  thresholdId,
  pathToPerformActionAt,
  originalRule,
}: {
  thresholdId: string;
  pathToPerformActionAt: string;
  originalRule: Rules | {};
}) {
  const booleanRule = cloneDeep(originalRule);
  if (typeof booleanRule === 'string') return booleanRule;
  if (!('id' in booleanRule)
    && !('and' in booleanRule) && !('or' in booleanRule)) {
    // no rules at all
    return {
      [booleanRuleStrings.AND_OPERATOR_STRING]: [
        { id: thresholdId },
      ],
    };
  }

  if ('id' in booleanRule) {
    // this was a simple flat indexed rule with 1 threshold
    const operator = pathToPerformActionAt === booleanRuleStrings.OR_OPERATOR_STRING
      ? booleanRuleStrings.OR_OPERATOR_STRING
      : booleanRuleStrings.AND_OPERATOR_STRING;
    return {
      [operator]: [
        {
          id: booleanRule.id,
        },
        {
          id: thresholdId,
        },
      ],
    };
  }

  const firstOperatorKeyInTheOriginalRule = 'and' in booleanRule
    ? booleanRuleStrings.AND_OPERATOR_STRING
    : booleanRuleStrings.OR_OPERATOR_STRING;
  // otherwise it has more than one threshold in the rule
  if (pathToPerformActionAt?.includes('[')) {
    // event was sent to a path that's more than simply and/or operator key,
    // meaning this threshold would have to be attached to a specific key value pair
    // path would be like this: sample 1- and[1] / sample 2- or[2].and
    const getPathData = getProperty(
      booleanRule,
      pathToPerformActionAt,
    ) as {
      id: string;
    }[];

    if (!getPathData?.length) {
      return undefined;
    }

    const updatedRule = setProperty(
      booleanRule,
      pathToPerformActionAt,
      [
        ...getPathData,
        {
          id: thresholdId,
        },
      ],
    );

    return updatedRule;
  }

  // we by default attach the new threshold onto the top level key unless path is specified
  booleanRule[firstOperatorKeyInTheOriginalRule].push({ id: thresholdId });
  return booleanRule;
}

export function deleteThresholdFromBooleanRule({
  thresholdId,
  originalRule,
}: {
  thresholdId: string;
  originalRule: Rules | {};
}) {
  const booleanRule = cloneDeep(originalRule);
  if (typeof booleanRule === 'string') return booleanRule;
  if ('id' in booleanRule) {
    // this was a simple flat indexed rule with 1 threshold
    return {};
  }

  const firstOperatorKeyInTheOriginalRule = 'and' in booleanRule
    ? booleanRuleStrings.AND_OPERATOR_STRING
    : booleanRuleStrings.OR_OPERATOR_STRING;
  // for removal we use recursive function to remove and also correctly reformat objects
  // if the threshold id being deleted was part of another and/or array and
  // after deletion there's only one element left in that array
  const updatedRule = Object.values(booleanRule).length > 0
    ? findThresholdIdAndUpdate({
      thresholdId,
      booleanRule: booleanRule[firstOperatorKeyInTheOriginalRule],
    })
    : {};

  if (updatedRule.length) {
    return {
      [firstOperatorKeyInTheOriginalRule]: updatedRule,
      publicId: isBooleanRule(booleanRule) ? booleanRule.publicId : undefined,
    };
  }

  return {};
}

export function correctAmbiguousBooleanRule({
  newRuleOperatorForCorrection,
  originalRule,
}: {
  newRuleOperatorForCorrection: 'and' | 'or';
  originalRule: Rules | {};
}) {
  const booleanRule = cloneDeep(originalRule);

  // these 2 handlings are just there for caution/satifying TS compiler, not really going to happen.
  if (typeof booleanRule === 'string') return { value: booleanRule };
  if ('id' in booleanRule) return booleanRule;

  let topLevelOperator;
  if ('and' in booleanRule) {
    topLevelOperator = 'and';
  } else if ('or' in booleanRule) {
    topLevelOperator = 'or';
  }

  if (!(topLevelOperator in booleanRule)) return booleanRule;
  return correctAmbiguousBooleanRuleObject(
    booleanRule as BooleanRule,
    newRuleOperatorForCorrection,
  );
}
